import React from 'react';
import flatten from 'lodash/flatten';
import { newDate, shipmentStatusLang } from '../../utils/formatters';
import { makeColumnConfig } from '../../utils/table';
import {
	ORDER_LIST_DOCUMENT_FIELDS,
	ORDER_LIST_ROW_FIELDS,
	ORDER_LIST_SHIPPING_FIELDS,
	ORDER_LIST_STATUS_HISTORY_FIELDS,
} from '../../apis/hooks/models/orderList';
import CellRowItem from './components/CellRowItem';
import Download from '../../components/Download';
import ShippingDateColumnCell from './components/ShippingDateColumnCell';
import StatusColumnCell from './components/StatusColumnCell';

/** @typedef {import('../../apis/hooks/models/orderList').OrderListRow} OrderListRow */
/** @typedef {import('../../apis/hooks/models/orderList').OrderListShippingInfo} OrderListShippingInfo */
/** @typedef {import('../../apis/hooks/models/orderList').OrderListDocument} OrderListDocument */
/** @typedef {import('../../apis/hooks/models/orderList').OrderListStatusHistory} OrderListStatusHistory */

/**
 * @param {OrderListRow} row
 * @returns {OrderListShippingInfo[]}
 */
const getShippingInfo = (row) =>
	row?.[ORDER_LIST_ROW_FIELDS.SHIPPING_INFO] || [];
/**
 * @template T
 * @param {OrderListRow} row
 * @param {(item: OrderListShippingInfo) => T} selector
 * @returns {T[]}
 */
const mapShippingInfo = (row, selector) => getShippingInfo(row).map(selector);

/**
 * @template T
 * @param {OrderListRow} row
 * @param {(item: OrderListShippingInfo) => T} selector
 * @returns {T[]}
 */
const getShippingValues = (row, selector) =>
	mapShippingInfo(row, selector).filter((item) => !!item);

/**
 * @template T
 * @param {OrderListRow} row
 * @param {(item: OrderListShippingInfo) => T | T[]} selector
 * @returns {T[]}
 */
const getShippingValuesFlat = (row, selector) =>
	flatten(getShippingValues(row, selector));

/**
 * @param {string} fieldName
 * @returns {(row: OrderListRow) => any[]}
 */
const makeShippingFlatGetter = (fieldName) => (row) =>
	getShippingValuesFlat(row, (item) => item[fieldName]);

/** @param {OrderListRow} row */
const getShippingStatuses = (row) =>
	getShippingValues(row, (item) => item[ORDER_LIST_SHIPPING_FIELDS.STATUS]);

const getShippingInvoiceNumbers = makeShippingFlatGetter(
	ORDER_LIST_SHIPPING_FIELDS.INVOICE_NUMBER
);
const getShippingBolNumbers = makeShippingFlatGetter(
	ORDER_LIST_SHIPPING_FIELDS.BOL
);
const getShippingCarrierEquipmentNumbers = makeShippingFlatGetter(
	ORDER_LIST_SHIPPING_FIELDS.CARRIER_EQUIPMENT_NUMBER
);

/** @param {OrderListRow} row */
const getInvoiceNumbers = (row) => {
	const shippingInvoices = getShippingInvoiceNumbers(row);
	if (shippingInvoices.length) {
		return shippingInvoices;
	}

	const rowInvoiceNumber = row?.[ORDER_LIST_ROW_FIELDS.INVOICE_NUMBER];
	return rowInvoiceNumber ? [rowInvoiceNumber] : [];
};

/** @param {OrderListRow} row */
const getShipDateColumnData = (row) => ({
	values: mapShippingInfo(row, (item) => ({
		bol: item[ORDER_LIST_SHIPPING_FIELDS.BOL],
		shipDate: item[ORDER_LIST_SHIPPING_FIELDS.SHIP_DATE],
	})),
});

/** @param {OrderListRow} row */
const getShipmentStatusValue = (row) => {
	const shippingStatuses = getShippingStatuses(row);
	return (
		row[ORDER_LIST_ROW_FIELDS.SHIPMENT_STATUS] || shippingStatuses[0] || ''
	);
};

/**
 * @param {OrderListRow} row
 * @returns {OrderListStatusHistory[]}
 */
const getStatusHistory = (row) => {
	const history = row?.[ORDER_LIST_ROW_FIELDS.ORDER_STATUS_HISTORY];
	if (Array.isArray(history)) {
		return history;
	}
	return Object.values(history || {});
};

/**
 * @param {OrderListRow} row
 * @returns {{
 * 	status: string,
 * 	statusHistory: Array<{status: string, bol: string}>,
 * 	shippingInfo: Array<{status: string | null, bol: string | undefined}>
 * }}
 */
const getStatusColumnData = (row) => {
	const rawHistory = getStatusHistory(row);

	const statusHistory = rawHistory
		.map((item) => ({
			status: item?.[ORDER_LIST_STATUS_HISTORY_FIELDS.ORDER_STATUS],
			bol: item?.[ORDER_LIST_STATUS_HISTORY_FIELDS.BOL_NUMBER] || '',
		}))
		.filter((item) => !!item.status);

	const shippingInfo = mapShippingInfo(row, (item) => ({
		status: item[ORDER_LIST_SHIPPING_FIELDS.STATUS] || null,
		bol: item[ORDER_LIST_SHIPPING_FIELDS.BOL],
	}));

	return {
		status: row?.[ORDER_LIST_ROW_FIELDS.STATUS] || '',
		statusHistory,
		shippingInfo,
	};
};

// Column object keys are also translation keys via makeColumnTranslation
// (for example, orders:columns*Order_Due_Date), so keep these names stable.
// Use sourceField for direct DTO mappings; use getter only for derived/normalized values.
const columnConfig = {
	PO: {
		sortOrder: 0,
		sourceField: ORDER_LIST_ROW_FIELDS.PO_NUMBER,
		isVisible: true,
		isSortable: true,
		isPersisted: true,
	},
	Status: {
		sortOrder: 1,
		getter: getStatusColumnData,
		formatter: (statusData) => <StatusColumnCell statusData={statusData} />,
		isVisible: true,
		isSortable: true,
	},
	Order_Due_Date: {
		sortOrder: 2,
		sourceField: ORDER_LIST_ROW_FIELDS.ORDER_DUE_DATE,
		formatter: newDate,
		isVisible: true,
		isSortable: true,
	},
	Ship_Date: {
		sortOrder: 3,
		/* eslint-disable camelcase */
		getter: getShipDateColumnData,
		formatter: (props) =>
			!!props.values.length && <ShippingDateColumnCell {...props} />,
		isVisible: true,
		isSortable: false,
	},
	DigitalDocuments: {
		sortOrder: 4,
		sourceField: ORDER_LIST_ROW_FIELDS.DIGITAL_DOCUMENTS,
		isVisible: true,
		/** @param {OrderListDocument[]} documents */
		formatter: (documents) =>
			!!documents.length && (
				<Download
					documents={documents.map((item) => ({
						id: item[ORDER_LIST_DOCUMENT_FIELDS.ID],
						type: item[ORDER_LIST_DOCUMENT_FIELDS.TYPE],
						fileName: item[ORDER_LIST_DOCUMENT_FIELDS.FILE_NAME],
					}))}
				/>
			),
	},
	Item_Description: {
		sortOrder: 5,
		sourceField: ORDER_LIST_ROW_FIELDS.ITEM_DESCRIPTION,
		isVisible: true,
		isSortable: true,
	},
	Design_Number: {
		sortOrder: 6,
		sourceField: ORDER_LIST_ROW_FIELDS.DESIGN_NUMBER,
		isVisible: true,
		isSortable: true,
	},
	Customer_Item_Number: {
		sortOrder: 7,
		sourceField: ORDER_LIST_ROW_FIELDS.CUSTOMER_ITEM_NUMBER,
		isVisible: true,
		isSortable: true,
	},
	Plant_Name: {
		sortOrder: 8,
		sourceField: ORDER_LIST_ROW_FIELDS.PLANT_NAME,
		isVisible: false,
		isSortable: false,
	},
	Order_Number: {
		sortOrder: 9,
		sourceField: ORDER_LIST_ROW_FIELDS.ORDER_NUMBER,
		isVisible: false,
		isSortable: true,
	},
	Invoice_Number: {
		sortOrder: 10,
		getter: getInvoiceNumbers,
		formatter: (values) =>
			!!values.length &&
			values.map((value) => <CellRowItem>{value}</CellRowItem>),
		isVisible: false,
	},
	Shipment_Status: {
		sortOrder: 11,
		getter: getShipmentStatusValue,
		formatter: shipmentStatusLang,
		isVisible: false,
		isSortable: true,
	},
	Order_Date: {
		sortOrder: 12,
		sourceField: ORDER_LIST_ROW_FIELDS.ORDER_DATE,
		formatter: newDate,
		isVisible: false,
		isSortable: true,
	},
	PO_Line_Number: {
		sortOrder: 13,
		sourceField: ORDER_LIST_ROW_FIELDS.PO_LINE_NUMBER,
		isVisible: false,
		isSortable: true,
	},
	Quantity_Ordered: {
		sortOrder: 14,
		sourceField: ORDER_LIST_ROW_FIELDS.QUANTITY_ORDERED,
		isVisible: false,
		isSortable: true,
	},
	Quantity_Shipped: {
		sortOrder: 15,
		sourceField: ORDER_LIST_ROW_FIELDS.QUANTITY_SHIPPED,
		isVisible: false,
		isSortable: true,
	},
	Quantity_Produced: {
		sortOrder: 16,
		sourceField: ORDER_LIST_ROW_FIELDS.QUANTITY_PRODUCED,
		isVisible: false,
		isSortable: true,
	},
	BOL: {
		sortOrder: 17,
		getter: getShippingBolNumbers,
		formatter: (values) =>
			!!values.length &&
			values.map((value) => (
				<CellRowItem key={Math.random()}>{value}</CellRowItem>
			)),
		isVisible: false,
	},
	Carrier_Equipment_Number: {
		sortOrder: 18,
		getter: getShippingCarrierEquipmentNumbers,
		formatter: (values) =>
			!!values.length &&
			values.map((value) => (
				<CellRowItem key={Math.random()}>{value}</CellRowItem>
			)),
		isVisible: false,
	},
};

export default makeColumnConfig(columnConfig);

export const columnOrder = Object.keys(columnConfig).sort(
	(a, b) => columnConfig[a].sortOrder > columnConfig[b].sortOrder
);

export const columnVisibility = Object.keys(columnConfig)
	.map((key) => ({ [key]: columnConfig[key].isVisible !== false }))
	.reduce((a, b) => ({ ...a, ...b }));
